import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  Camera,
  ClipboardList,
  CreditCard,
  DollarSign,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  Receipt,
  Ruler,
  Sparkles,
} from "lucide-react";
import { ScheduleAppointmentDialog } from "./ScheduleAppointmentDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface OverviewTabProps {
  job: any;
  photos: any[];
  measurements: any;
  notes: any[];
  tasks: any[];
  documents: any[];
  jobTrades?: any[];
  timeline?: any[];
  payments?: any[];
  onAddNote: () => void;
  onAddTask: () => void;
  onTabChange: (tab: string) => void;
  onMeasureJob?: () => void;
  onOpenPayments?: () => void;
  onOpenTimeline?: () => void;
  onOpenDocuments?: () => void;
  onOpenTasks?: () => void;
  onOpenCommunications?: () => void;
  onOpenAIReport?: () => void;
}

const formatMoney = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value || 0);

const isCompleteStatus = (status?: string | null) =>
  ["completed", "complete", "done", "paid"].includes((status || "").toLowerCase());

export const OverviewTab = ({
  job,
  photos,
  measurements,
  notes,
  tasks,
  documents,
  jobTrades,
  timeline = [],
  payments = [],
  onAddNote,
  onAddTask,
  onTabChange,
  onMeasureJob,
  onOpenPayments,
  onOpenTimeline,
  onOpenDocuments,
  onOpenTasks,
  onOpenCommunications,
  onOpenAIReport,
}: OverviewTabProps) => {
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);

  const roofReports = Array.isArray(measurements?.roof) ? measurements.roof : [];
  const totalSquares = Number(roofReports[0]?.total_squares || 0);
  const displaySquares = Math.round(totalSquares);
  const photosCount = photos?.length || 0;
  const tradeCount = jobTrades?.length || 0;
  const totalEstimate = jobTrades?.reduce((sum, trade) => sum + (Number(trade.subtotal) || 0), 0) || 0;
  const completedPayments = payments.filter((p) => isCompleteStatus(p.status));
  const collectedTotal = completedPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const pendingPaymentsTotal = payments
    .filter((p) => (p.status || "").toLowerCase() === "pending")
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const openTasks = tasks.filter((t) => !isCompleteStatus(t.status));
  const completedTasks = tasks.filter((t) => isCompleteStatus(t.status));
  const estimateTab = job.property_type === "Commercial" ? "Quick Estimate" : "Measurement/Estimate";

  const recentTimeline = timeline.slice(0, 5);
  const recentNotes = notes.slice(0, 3);
  const latestNote = recentNotes[0];
  const olderRecentNotes = recentNotes.slice(1);
  const recentPayments = payments.slice(0, 3);

  const priorityActions = [
    !displaySquares && {
      title: "Pull or upload measurements",
      detail: "Use satellite measurement or import EagleView, Roofr, Hover, GAF, or another PDF before pricing.",
      cta: "Open measurements",
      onClick: onMeasureJob,
    },
    !photosCount && {
      title: "Capture site documentation",
      detail: "Add exterior, damage, and progress photos to the job record.",
      cta: "Open photos",
      onClick: () => onTabChange("Photo Hub"),
    },
    totalEstimate > 0 && payments.length === 0 && {
      title: "Log the first payment",
      detail: "Priced scope exists but no deposit or payment is on file.",
      cta: "Record payment",
      onClick: onOpenPayments,
    },
    openTasks.length > 0 && {
      title: `Resolve ${openTasks.length} open task${openTasks.length === 1 ? "" : "s"}`,
      detail: "Review outstanding follow-ups and handoff items.",
      cta: "Review tasks",
      onClick: onOpenTasks,
    },
  ].filter(Boolean) as Array<{ title: string; detail: string; cta: string; onClick?: () => void }>;

  const stats = [
    {
      label: "Total estimate",
      value: totalEstimate ? formatMoney(totalEstimate) : "—",
      sub: tradeCount ? `${tradeCount} trade${tradeCount === 1 ? "" : "s"} priced` : "Build the estimate",
      icon: DollarSign,
      tone: "from-emerald-500/15 to-emerald-500/5 border-emerald-500/30",
      iconBg: "bg-emerald-500 text-white",
      accent: "text-emerald-500",
    },
    {
      label: "Collected",
      value: formatMoney(collectedTotal),
      sub: pendingPaymentsTotal ? `${formatMoney(pendingPaymentsTotal)} pending` : "No pending payments",
      icon: CreditCard,
      tone: "from-primary/15 to-primary/5 border-primary/30",
      iconBg: "bg-primary text-primary-foreground",
      accent: "text-primary",
    },
    {
      label: "Roof area",
      value: displaySquares ? `${displaySquares} sq` : "—",
      sub: displaySquares ? `${roofReports.length} measurement report${roofReports.length === 1 ? "" : "s"}` : "Measurement not loaded",
      icon: Ruler,
      tone: "from-violet-500/15 to-violet-500/5 border-violet-500/30",
      iconBg: "bg-violet-500 text-white",
      accent: "text-violet-500",
    },
    {
      label: "Tasks",
      value: `${openTasks.length} open`,
      sub: completedTasks.length ? `${completedTasks.length} completed` : "Nothing closed yet",
      icon: ClipboardList,
      tone: "from-amber-500/15 to-amber-500/5 border-amber-500/30",
      iconBg: "bg-amber-500 text-white",
      accent: "text-amber-600",
    },
  ];

  // 3D folder-tab style action buttons
  const quickActions = [
    { label: "Measure", icon: Ruler, onClick: onMeasureJob, disabled: !onMeasureJob, base: "violet-500", glow: "violet-400" },
    { label: "Estimate", icon: DollarSign, onClick: () => onTabChange(estimateTab), disabled: false, base: "emerald-500", glow: "emerald-400" },
    { label: "AI Report", icon: Sparkles, onClick: onOpenAIReport, disabled: !onOpenAIReport, base: "fuchsia-500", glow: "fuchsia-400" },
    { label: "Payment", icon: Receipt, onClick: onOpenPayments, disabled: !onOpenPayments, base: "primary", glow: "primary" },
    { label: "Note", icon: MessageSquare, onClick: onAddNote, disabled: false, base: "sky-500", glow: "sky-400" },
    { label: "Task", icon: ClipboardList, onClick: onAddTask, disabled: false, base: "amber-500", glow: "amber-400" },
    { label: "Documents", icon: FileText, onClick: onOpenDocuments, disabled: !onOpenDocuments, base: "slate-700", glow: "slate-500" },
  ];

  const handleCall = () => job.owner_phone && (window.location.href = `tel:${job.owner_phone}`);
  const handleSms = () => job.owner_phone && (window.location.href = `sms:${job.owner_phone}`);
  const handleEmail = () => job.owner_email && (window.location.href = `mailto:${job.owner_email}`);

  return (
    <div className="px-4 py-5 md:px-6 md:py-6 space-y-6">
      {/* 1. STAT CARDS */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className={`relative overflow-hidden border bg-gradient-to-br ${stat.tone} p-5`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/80">{stat.label}</div>
                  <div className={`mt-2 text-3xl font-display font-bold tabular-nums ${stat.accent}`}>{stat.value}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{stat.sub}</p>
                </div>
                <div className={`rounded-xl p-2.5 shadow-md ${stat.iconBg}`}>
                  <Icon className="h-5 w-5" strokeWidth={2.5} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* 2. 3D FOLDER-TAB ACTION BUTTONS */}
      <div className="grid grid-cols-7 gap-2">
        {quickActions.map((action) => {
          const Icon = action.icon;
          const colorMap: Record<string, { bg: string; border: string; ring: string; shadow: string }> = {
            "violet-500":  { bg: "from-violet-400 to-violet-600",   border: "border-violet-700",   ring: "ring-violet-300/40",  shadow: "shadow-violet-500/40" },
            "emerald-500": { bg: "from-emerald-400 to-emerald-600", border: "border-emerald-700",  ring: "ring-emerald-300/40", shadow: "shadow-emerald-500/40" },
            "primary":     { bg: "from-primary to-primary/80",      border: "border-primary",      ring: "ring-primary/40",     shadow: "shadow-primary/40" },
            "sky-500":     { bg: "from-sky-400 to-sky-600",         border: "border-sky-700",      ring: "ring-sky-300/40",     shadow: "shadow-sky-500/40" },
            "amber-500":   { bg: "from-amber-400 to-amber-600",     border: "border-amber-700",    ring: "ring-amber-300/40",   shadow: "shadow-amber-500/40" },
            "slate-700":   { bg: "from-slate-600 to-slate-800",     border: "border-slate-900",    ring: "ring-slate-400/40",   shadow: "shadow-slate-700/40" },
            "fuchsia-500": { bg: "from-fuchsia-400 to-fuchsia-600", border: "border-fuchsia-700", ring: "ring-fuchsia-300/40", shadow: "shadow-fuchsia-500/40" },
          };
          const c = colorMap[action.base];
          return (
            <button
              key={action.label}
              onClick={action.onClick}
              disabled={action.disabled}
              className={`group relative isolate flex flex-col items-center justify-center gap-1.5 rounded-xl border-b-4 px-2 py-3 text-xs font-semibold text-white bg-gradient-to-b ${c.bg} ${c.border} shadow-lg ${c.shadow} ring-1 ${c.ring} transition-all duration-150 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0.5 active:border-b-2 active:shadow-md disabled:opacity-40 disabled:hover:translate-y-0 disabled:cursor-not-allowed`}
            >
              <span className="absolute inset-x-0 top-0 h-1/2 rounded-t-xl bg-gradient-to-b from-white/25 to-transparent pointer-events-none" />
              <Icon className="h-5 w-5 drop-shadow" strokeWidth={2.5} />
              <span className="drop-shadow-sm truncate">{action.label}</span>
            </button>
          );
        })}
      </div>

      {/* 3. OPEN ITEMS */}
      {priorityActions.length > 0 && (
        <Card className="border-border/60 p-5 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Open items</h3>
              <p className="text-sm text-muted-foreground">Next moves to keep this job progressing.</p>
            </div>
            <Badge variant="outline" className="border-border/70 text-muted-foreground">
              {priorityActions.length} active
            </Badge>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {priorityActions.slice(0, 6).map((item) => (
              <div key={item.title} className="rounded-lg border border-border/70 bg-muted/20 p-4">
                <p className="font-medium text-foreground">{item.title}</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.detail}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={item.onClick} disabled={!item.onClick}>
                  {item.cta}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 4. TIMELINE + COMMUNICATIONS */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="border-border/60 p-5 md:p-6 lg:col-span-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Timeline</h3>
              <p className="text-sm text-muted-foreground">Recent activity and job updates.</p>
            </div>
            <Button variant="outline" size="sm" onClick={onOpenTimeline} disabled={!onOpenTimeline}>
              Full timeline
            </Button>
          </div>
          <div className="mt-5 space-y-4">
            {recentTimeline.length > 0 ? (
              recentTimeline.map((event: any, index) => (
                <div key={event.id || `${event.created_at}-${index}`} className="flex gap-3">
                  <div className="mt-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                  <div className="min-w-0 flex-1 border-b border-border/60 pb-4 last:border-b-0 last:pb-0">
                    <p className="text-sm font-medium text-foreground">
                      {event.description || event.event_type || "Update"}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {event.created_at ? formatDistanceToNow(new Date(event.created_at), { addSuffix: true }) : "Just now"}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-lg border border-dashed border-border/70 p-6 text-sm text-muted-foreground">
                No timeline events yet.
              </div>
            )}
          </div>
        </Card>

        <Card className="border-border/60 p-5 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Communications</h3>
              <p className="text-sm text-muted-foreground">Homeowner contact and outreach.</p>
            </div>
            <Button variant="outline" size="sm" onClick={onOpenCommunications} disabled={!onOpenCommunications}>
              View all
            </Button>
          </div>
          <div className="mt-5 grid gap-2">
            <Button variant="outline" size="sm" className="gap-2 justify-start" onClick={handleCall} disabled={!job.owner_phone}>
              <Phone className="h-4 w-4" /> Call {job.owner_phone || ""}
            </Button>
            <Button variant="outline" size="sm" className="gap-2 justify-start" onClick={handleSms} disabled={!job.owner_phone}>
              <MessageSquare className="h-4 w-4" /> Text
            </Button>
            <Button variant="outline" size="sm" className="gap-2 justify-start" onClick={handleEmail} disabled={!job.owner_email}>
              <Mail className="h-4 w-4" /> Email
            </Button>
          </div>
        </Card>
      </div>

      {/* 4b. JOB NOTES — sticky note row, full width */}
      <Card className="relative overflow-hidden border-border/60 bg-accent/70 p-5 shadow-lg shadow-primary/10 md:p-6">
        <div className="pointer-events-none absolute right-0 top-0 h-16 w-16 bg-gradient-to-bl from-primary/20 via-primary/10 to-transparent" />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Job notes</h3>
            <p className="text-sm text-muted-foreground">Latest field notes, call summaries, and quick updates.</p>
          </div>
          <Button size="sm" onClick={onAddNote}>
            Add note
          </Button>
        </div>

        <div className="relative mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 pt-2">
          {recentNotes.length > 0 ? (
            recentNotes.map((note: any, index) => {
              // Rotate between yellow, pink, blue, green post-it colors
              const palette = [
                { bg: "bg-yellow-200", text: "text-yellow-950", meta: "text-yellow-900/70", shadow: "shadow-yellow-900/30" },
                { bg: "bg-pink-200",   text: "text-pink-950",   meta: "text-pink-900/70",   shadow: "shadow-pink-900/30" },
                { bg: "bg-sky-200",    text: "text-sky-950",    meta: "text-sky-900/70",    shadow: "shadow-sky-900/30" },
                { bg: "bg-lime-200",   text: "text-lime-950",   meta: "text-lime-900/70",   shadow: "shadow-lime-900/30" },
                { bg: "bg-orange-200", text: "text-orange-950", meta: "text-orange-900/70", shadow: "shadow-orange-900/30" },
              ];
              const c = palette[index % palette.length];
              const rotations = ["-rotate-2", "rotate-1", "-rotate-1", "rotate-2", "rotate-0"];
              const rot = rotations[index % rotations.length];
              return (
                <div
                  key={note.id || `${note.created_at}-${index}`}
                  className={`relative ${c.bg} ${c.text} ${rot} p-4 pt-6 shadow-xl ${c.shadow} transition-transform hover:rotate-0 hover:scale-[1.02] hover:z-10`}
                  style={{
                    fontFamily: "'Caveat', 'Bradley Hand', 'Comic Sans MS', cursive",
                    minHeight: "180px",
                    boxShadow: "0 10px 20px -8px rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.15)",
                  }}
                >
                  {/* Tape strip at top */}
                  <div className="pointer-events-none absolute -top-2 left-1/2 h-5 w-16 -translate-x-1/2 bg-white/50 border border-white/60 shadow-sm" style={{ transform: "translateX(-50%) rotate(-3deg)" }} />
                  <div className={`flex items-center justify-between gap-2 text-[11px] uppercase tracking-wider ${c.meta}`} style={{ fontFamily: "inherit" }}>
                    <span className="font-bold">{note.note_type || "note"}</span>
                    <span>
                      {note.created_at ? formatDistanceToNow(new Date(note.created_at), { addSuffix: true }) : "Just now"}
                    </span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-xl leading-snug line-clamp-6">{note.content}</p>
                </div>
              );
            })
          ) : (
            <div className="rounded-2xl border border-dashed border-border/70 bg-background/70 p-6 text-sm text-muted-foreground sm:col-span-2 lg:col-span-3">
              No notes logged yet.
            </div>
          )}
        </div>
      </Card>

      {/* 5. RECENT PAYMENTS */}
      {recentPayments.length > 0 && (
        <Card className="border-border/60 p-5 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Recent payments</h3>
              <p className="text-sm text-muted-foreground">Latest activity on this job's ledger.</p>
            </div>
            <Button variant="outline" size="sm" onClick={onOpenPayments} disabled={!onOpenPayments}>
              Record payment
            </Button>
          </div>
          <div className="mt-5 space-y-3">
            {recentPayments.map((payment: any, index) => (
              <div key={payment.id || `${payment.created_at}-${index}`} className="flex items-center justify-between gap-3 rounded-lg border border-border/70 bg-muted/20 p-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground capitalize">
                    {payment.payment_method || payment.method || "Payment"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {(payment.status || "pending").replace(/_/g, " ")} •{" "}
                    {payment.payment_date || payment.created_at
                      ? formatDistanceToNow(new Date(payment.payment_date || payment.created_at), { addSuffix: true })
                      : "Just now"}
                  </p>
                </div>
                <div className="text-sm font-semibold text-foreground tabular-nums">
                  {formatMoney(Number(payment.amount || 0))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <ScheduleAppointmentDialog
        open={showScheduleDialog}
        onOpenChange={setShowScheduleDialog}
        jobId={job.id}
        jobAddress={job.property_address || undefined}
        ownerName={job.owner_name || undefined}
        onSuccess={() => window.location.reload()}
      />
    </div>
  );
};
